import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Plus, CheckCircle, AlertTriangle, ChevronDown } from 'lucide-react';
import axios from '../../utils/axios';
import { toastAPI } from '../../contexts/ToastContext';
import { pickSafeRussianBackendMessage, pickHttpStatusRussianFallback } from '../../utils/safeRussianBackendMessage';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { getSystemCompanyContextLabel } from '../../utils/systemCompany';
import { companyMembershipRoleLabel } from '../../utils/companyMembershipRoleLabel';

const POPOVER_Z = 1200;

const CompanySwitcher = ({ placement = 'bottomRight' }) => {
  const { company, refreshAuth, contextReady } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);

  const updatePopoverPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(340, Math.max(280, window.innerWidth - 24));
    const preferRight = placement === 'bottomRight';
    let left = preferRight ? rect.right - width : rect.left;
    left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
    const top = Math.min(rect.bottom + 8, window.innerHeight - 24);
    setPopoverStyle({
      position: 'fixed',
      top,
      left,
      width,
      zIndex: POPOVER_Z,
      maxHeight: Math.max(200, window.innerHeight - top - 16),
      overflow: 'auto',
    });
  }, [placement]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopoverPosition();
    const onWin = () => updatePopoverPosition();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Убираем старый .css импорт если он дублировал стили, но kanban и тд идут из STYLESYSTEM.
  // @import url('./STYLESYSTEM/interfaces/profile/company-switcher.css') уже в index.css

  const loadCompanies = useCallback(async () => {
    if (!contextReady || !company?.id) return;
    setListLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get('/api/connected-companies', { errorPresentation: 'inline', cacheTtlMs: 0, noDedup: true });
      if (response?.data?.success !== true || !Array.isArray(response.data.data)) throw new Error('COMPANY_LIST_UNAVAILABLE');
      setCompanies(response.data.data);
    } catch (_) {
      setLoadError('Не удалось загрузить компании. Повторите попытку.');
    } finally {
      setListLoading(false);
    }
  }, [contextReady, company?.id]);

  useEffect(() => {
    setCurrentCompanyId(company?.id || null);
    if (open && contextReady && company?.id) loadCompanies();
  }, [open, contextReady, company?.id, loadCompanies]);

  const handleSwitchCompany = async (targetCompanyId) => {
    if (String(targetCompanyId) === String(currentCompanyId || '')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/connected-companies/switch', {
        company_id: targetCompanyId,
      }, { errorPresentation: 'inline' });

      // Optimistic UI: лейбл и active-check сразу по списку компаний,
      // не ждём Redux /me (иначе гонка soft-SPA: старое имя + уже новый company_id в API).
      setCurrentCompanyId(targetCompanyId);
      setOpen(false);

      // Очищаем кэш axios для избежания утечек данных между компаниями
      axios.clearGetCache();

      // ВАЖНО: пока company-context на FILES/CORE-стеках кэшируется (5 сек),
      // в окне между switch → reload UI может успеть запросить FILES старой
      // зоны. Поднимаем флаг на 15 секунд: axios-interceptor добавит
      // X-Refresh-Company-Context: 1 к каждому запросу, и backend обойдёт кэш.
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(
            'mb:companyContextRefreshUntil',
            String(Date.now() + 15000),
          );
        }
      } catch (_) { /* sessionStorage может быть недоступен — это не критично */ }

      toastAPI?.success?.('Пространство изменено');

      await refreshAuth();
      // Без setTimeout(500): окно рассинхрона лейбла ↔ API закрываем немедленным reload.
      window.location.reload();
    } catch (error) {
      toastAPI?.error?.(
        pickSafeRussianBackendMessage(
          error?.response?.data?.error || error?.response?.data?.message,
          pickHttpStatusRussianFallback(error?.response?.status)
        )
      );
      // Откат optimistic id к сессии /me или connected-companies/current
      setCurrentCompanyId(company?.id || null);
    } finally {
      setLoading(false);
    }
  };

  const hasPrimaryCompany = !!company?.id;

  const currentListCompany = companies.find((c) => {
    const id = c.company_id || c.connected_company_id;
    return id && String(id) === String(currentCompanyId || '');
  });

  const displayCompanyName =
    currentListCompany?.custom_name
    || currentListCompany?.company_name
    || company?.name
    || 'Мои Компании';
  const displayCompanyId = currentCompanyId || company?.id || null;

  const triggerLogoUrl = (
    company?.settings?.branding?.logo_url
    || currentListCompany?.custom_logo_url
    || currentListCompany?.logo_url
    || null
  );

  const renderDropdownContent = () => {
    if (listLoading || loadError) {
      return (
        <div className="mb-company-switcher">
          <div className="mb-company-switcher-header" role={loadError ? 'alert' : 'status'}>
            <span className="mb-company-switcher-header-title">{loadError || 'Загружаем компании…'}</span>
          </div>
          {loadError && <button type="button" className="mb-company-switcher-action" onClick={loadCompanies}>Повторить</button>}
          <button type="button" className="mb-company-switcher-action" onClick={() => { setOpen(false); navigate('/company/requests'); }}>Запросы и обращения</button>
        </div>
      );
    }
    if (companies.length === 0) {
      return (
        <div className="mb-company-switcher">
          <div className="mb-company-switcher-header">
            <span className="mb-company-switcher-header-title">
              {hasPrimaryCompany ? 'Подключённых компаний пока нет' : 'У вас нет активных компаний'}
            </span>
          </div>
          <div className="mb-company-switcher-footer">
            <button
              className="mb-company-switcher-action"
              onClick={() => {
                setOpen(false);
                navigate('/company/create');
              }}
            >
              <Plus size={14} /> Создать компанию
            </button>
            <button
              className="mb-company-switcher-action"
              onClick={() => { setOpen(false); navigate('/company/requests'); }}
              style={{ marginTop: 4 }}
            >
              Запросы и обращения
            </button>
          </div>
        </div>
      );
    }

    return (
    <div className="mb-company-switcher">
      <div className="mb-company-switcher-header">
        <span className="mb-company-switcher-header-title">Ваши компании</span>
        <span className="mb-company-switcher-header-count">{companies.length}</span>
      </div>
      
      <div className="mb-company-switcher-list">
        {companies.map(c => {
          const companyId = c.company_id || c.connected_company_id;
          const isActive = String(companyId || '') === String(currentCompanyId || '');
          const blocked = c.status === 'blocked';
          const contextLabel = getSystemCompanyContextLabel(companyId);
          
          return (
             <div 
               key={companyId || c.id} 
               className={`mb-company-switcher-item ${isActive ? 'is-active' : ''} ${blocked ? 'is-blocked' : ''}`}
               role="button"
               tabIndex={blocked ? -1 : 0}
               aria-disabled={blocked}
               onClick={() => !blocked && companyId && handleSwitchCompany(companyId)}
               onKeyDown={(event) => {
                 if (!blocked && companyId && (event.key === 'Enter' || event.key === ' ')) {
                   event.preventDefault();
                   handleSwitchCompany(companyId);
                 }
               }}
             >
               <div className="mb-company-switcher-item-avatar">
                 {c.custom_logo_url || c.logo_url ? (
                   <img src={c.custom_logo_url || c.logo_url} alt={c.company_name} />
                 ) : (
                   c.company_name?.charAt(0).toUpperCase() || <Building2 size={16} />
                 )}
               </div>
               
               <div className="mb-company-switcher-item-content">
                 <div className="mb-company-switcher-item-title">
                   {c.custom_name || c.company_name}
                   {contextLabel && (
                     <span className="mb-company-switcher-item-context">{contextLabel}</span>
                   )}
                 </div>
                 <div className="mb-company-switcher-item-role">
                   {c.role_name || companyMembershipRoleLabel(c.role)}
                 </div>
               </div>

               {isActive && (
                 <div className="mb-company-switcher-item-check mb-company-switcher-item-check--success">
                   <CheckCircle size={16} />
                 </div>
               )}
               {blocked && (
                 <div title="Доступ ограничен" className="mb-company-switcher-item-warning">
                  <AlertTriangle size={16} className="mb-company-switcher-item-warning-icon" />
                 </div>
               )}
             </div>
          );
        })}
      </div>
      
      <div className="mb-company-switcher-footer">
        <button 
          className="mb-company-switcher-action"
          onClick={() => { setOpen(false); navigate('/company/create'); }}
        >
          <Plus size={14} /> Создать компанию
        </button>
        <button
          className="mb-company-switcher-action"
          onClick={() => { setOpen(false); navigate('/company/requests'); }}
          style={{ marginTop: 4 }}
        >
          Запросы и обращения
        </button>
      </div>
    </div>
    );
  };

  return (
    <div ref={wrapRef} className="mb-company-switcher-wrap">
      <button 
        type="button"
        className="mb-btn-icon mb-company-switcher-trigger"
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          updatePopoverPosition();
          setOpen(true);
        }}
        disabled={loading}
        aria-busy={loading}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={loading ? 'Смена пространства компании' : (displayCompanyName ? `Компания: ${displayCompanyName}` : 'Выбор компании')}
        title={displayCompanyName || 'Мои компании'}
      >
        {triggerLogoUrl ? (
          <img
            className="mb-company-switcher-trigger-logo"
            src={triggerLogoUrl}
            alt=""
            width={22}
            height={22}
          />
        ) : (
          <Building2 size={16} className="mb-company-switcher-trigger-icon" aria-hidden />
        )}
        <span className="mb-company-switcher-trigger-label">
          {loading ? 'Смена пространства…' : (
            <>
              {displayCompanyName}
              {displayCompanyId && getSystemCompanyContextLabel(displayCompanyId) && (
                <span className="mb-company-switcher-trigger-context">
                  {getSystemCompanyContextLabel(displayCompanyId)}
                </span>
              )}
            </>
          )}
        </span>
        {loading ? (
          <span className="mb-spin mb-spin--sm" aria-hidden />
        ) : (
          <ChevronDown size={14} className="mb-company-switcher-trigger-chevron" aria-hidden />
        )}
      </button>

      {open &&
        popoverStyle &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              className="mb-company-switcher-overlay"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <div
              ref={popoverRef}
              className="mb-company-switcher-popover mb-company-switcher-popover--portal"
              style={popoverStyle}
              role="dialog"
              aria-label="Ваши компании"
            >
              {renderDropdownContent()}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default CompanySwitcher;
